import { daysStale, dspLabel, freshnessByDsp } from '../format';

/**
 * The freshness line above Performance Overview (#4586).
 *
 * The old message headlined the OLDEST platform for every platform: one DSP 23
 * days behind rendered as "All platforms reported through 23 days ago" while the
 * other eight were current to 2 days. It then listed all nine, green and red
 * together, so the one that needed attention sat among eight that did not.
 *
 * This says only what is wrong, and only when something is. Pure so the wording
 * rules can be tested without mounting the header.
 */

/** More than this many days without reporting is "behind" — unchanged from #4464. */
export const STALE_DAYS_THRESHOLD = 15;

export interface PlatformFreshness {
  dsp: string;
  iso: string;
  days: number;
}

export interface FreshnessMessage {
  /** Red. Null when nothing is behind. */
  warning: string | null;
  /** Grey. Null when every platform is behind — the warning already said it. */
  detail: string | null;
  /** Behind, oldest first. Drives the info-icon list. */
  stale: PlatformFreshness[];
  /** Current, oldest first. */
  fresh: PlatformFreshness[];
}

/**
 * "Sep 23" — a calendar date, not "2 days ago" (#4586). A reader comparing the
 * page against a platform's own UI needs the date the platform states, and a
 * relative label forces them to do the arithmetic. The day count survives only
 * in the red warning, where the magnitude is the point.
 */
export const calendarDate = (iso: string): string => {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return iso;
  const [y, m, d] = iso.split('-').map(Number);
  // Construct locally rather than from the parsed ms: an ISO date-only string
  // parses as UTC, which renders as the previous day west of Greenwich.
  const date = Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d) ? new Date(y, m - 1, d) : new Date(ms);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * Tercept re-sends the trailing days on every daily delivery, so their totals can
 * still change: TikTok's first delivery runs ~24% low and DV360's can arrive empty
 * (#4851). The window is Tercept's, not ours; change it here if theirs changes.
 */
export const RESTATEMENT_DAYS = 5;

export interface ProvisionalNote {
  /** Inline, after "Data through …". */
  short: string;
  /** Tooltip sentence naming the dates. */
  detail: string;
}

const dayRange = (from: Date, to: Date): string => {
  const fmt = (d: Date, withMonth: boolean) =>
    withMonth ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : String(d.getDate());
  return `${fmt(from, true)}–${fmt(to, from.getMonth() !== to.getMonth())}`;
};

/**
 * Anchored on `rows[0]`, the OLDEST platform date — the same date
 * `freshnessMessage`'s "Data through …" uses, since `rows` is oldest-first.
 * Anchoring on the newest date instead (review on #5036) disagreed with
 * "Data through …" whenever platforms don't share a date, which is the
 * common case: e.g. {beeswax: Oct 15, dv360: Oct 13} read "Data through
 * Oct 13 · last 5 days may still change" with a tooltip window of Oct
 * 11–15, which a reader takes as relative to Oct 13, not Oct 15.
 */
export const provisionalNote = (freshness: Record<string, string>): ProvisionalNote | null => {
  const rows = freshnessByDsp(freshness);
  if (rows.length === 0) return null;
  const [y, m, d] = rows[0].iso.split('-').map(Number);
  if (![y, m, d].every(Number.isFinite)) return null;
  const to = new Date(y, m - 1, d);
  const from = new Date(y, m - 1, d - (RESTATEMENT_DAYS - 1));
  return {
    short: `last ${RESTATEMENT_DAYS} days may still change`,
    detail: `Platforms update the last ${RESTATEMENT_DAYS} days (${dayRange(from, to)}) as late data arrives. Totals for those days can still go up.`,
  };
};

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

export const freshnessMessage = (freshness: Record<string, string>): FreshnessMessage => {
  const rows = freshnessByDsp(freshness)
    .map(({ dsp, iso }) => ({ dsp, iso, days: daysStale(iso) ?? 0 }))
    .filter(r => Number.isFinite(r.days));

  if (rows.length === 0) {
    return { warning: null, detail: 'Reporting data is being prepared', stale: [], fresh: [] };
  }

  const stale = rows.filter(r => r.days > STALE_DAYS_THRESHOLD);
  const fresh = rows.filter(r => r.days <= STALE_DAYS_THRESHOLD);

  // Healthy: one grey line at the oldest date. `rows` is oldest-first, so [0]
  // is the date the combined report is actually complete through.
  if (stale.length === 0) {
    return { warning: null, detail: `Data through ${calendarDate(rows[0].iso)}`, stale, fresh };
  }

  const oldest = stale[0];

  // Everything is behind: there is no "other platforms" to reassure anyone with,
  // and a grey line claiming some are current would be false.
  if (fresh.length === 0) {
    return {
      warning: `No platform has reported since ${calendarDate(oldest.iso)} (${plural(oldest.days, 'day')})`,
      detail: null,
      stale,
      fresh,
    };
  }

  const detail = `Other ${plural(fresh.length, 'platform')} current through ${calendarDate(fresh[0].iso)}`;

  // One or two are worth naming. Beyond that the names crowd out the number,
  // and the count is what tells you how bad it is.
  if (stale.length === 1) {
    return {
      warning: `${dspLabel(oldest.dsp)} hasn't reported since ${calendarDate(oldest.iso)} (${plural(oldest.days, 'day')})`,
      detail,
      stale,
      fresh,
    };
  }
  if (stale.length === 2) {
    return {
      warning: `${dspLabel(stale[0].dsp)} and ${dspLabel(stale[1].dsp)} are behind (oldest ${calendarDate(oldest.iso)})`,
      detail,
      stale,
      fresh,
    };
  }
  return {
    warning: `${plural(stale.length, 'platform')} are behind (oldest ${calendarDate(oldest.iso)})`,
    detail,
    stale,
    fresh,
  };
};
